import React, { useState } from 'react';

import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';

import Button from 'components/Button/Button';
import { Popover, PopoverTrigger } from 'components/ui';
import { SidebarContent, SidebarMenu } from 'components/ui/sidebar';

import { TemplateStudioFooterForm } from './BlocksForms/Footer';
import { TemplateStudioHeaderForm } from './BlocksForms/Header';
import { TemplateStudioAddBlockModal } from './Components/AddBlockModal';
import { FormsSeparator } from './Components/FormMenuSeparator';
import { TemplateStudioDraggableBlockForms } from './DraggableBlockForms';

export const MAIN_SIDEBAR_WIDTH = 318;

export const TemplateStudioMainSidebar: React.FC = () => {
  const { publisherId } = useParams<{ publisherId: string }>();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const canAddBlock = !!featureFlags?.[EFeatureFlag.DASHBOARD_ROLLING_OFFER_1N_ENABLED];

  const { defaultBodyBlocksOrder } = useTemplateStudioState();
  const [isAddBlockOpen, setIsAddBlockOpen] = useState(false);

  return (
    <SidebarContent
      className={`tb-main-sidebar-content w-[${MAIN_SIDEBAR_WIDTH}px] max-w-[${MAIN_SIDEBAR_WIDTH}px] min-w-[${MAIN_SIDEBAR_WIDTH}px] h-full box-content`}
    >
      {defaultBodyBlocksOrder && (
        <SidebarMenu className="tb-main-sidebar-menu gap-0 pl-1 pr-4">
          <div className="pl-[16px]">
            <div className="flex items-center justify-between gap-3 border-b border-neutral-200 pb-3 mb-1 pr-2">
              <span className="text-p-s font-medium text-base-popoverForeground">
                Blocks
              </span>
              {canAddBlock && (
                <Popover open={isAddBlockOpen} onOpenChange={setIsAddBlockOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      className="h-7 w-[109px] shrink-0 justify-center gap-2 rounded-md bg-indigo-500 px-3 text-p-s font-medium leading-5 text-white shadow-sm hover:bg-indigo-600 [&_i.font-awesome-icon]:!text-[12px] [&_i.font-awesome-icon]:!leading-none"
                      leftIcon={
                        <FontAwesomeIcon
                          icon="fa-regular fa-plus"
                          className="!text-[12px] !leading-none"
                        />
                      }
                      dataTestId="TemplatesStudio-AddBlock-open"
                    >
                      Add Block
                    </Button>
                  </PopoverTrigger>
                  <TemplateStudioAddBlockModal
                    onClose={() => setIsAddBlockOpen(false)}
                    enabledBlocks={defaultBodyBlocksOrder}
                  />
                </Popover>
              )}
            </div>
          </div>

          <TemplateStudioHeaderForm />
          <FormsSeparator />

          <TemplateStudioDraggableBlockForms
            defaultBodyBlocksOrder={defaultBodyBlocksOrder}
          />

          <TemplateStudioFooterForm />
        </SidebarMenu>
      )}
    </SidebarContent>
  );
};
